import NavigationParticuliers from '@/components/NavigationParticuliers';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import UniformForm from '@/components/UniformForm';
import { submitDeliveryRequest } from '@/services/brevoEmailService';
import { ScrollReveal } from '@/components/ScrollReveal';
import { Truck, MapPin, Clock, CheckCircle } from 'lucide-react';

const DemandeLivraison = () => {
  const formFields = [
    {
      id: 'nom',
      label: 'Nom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'prenom',
      label: 'Prénom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email' as const,
      required: true
    },
    {
      id: 'telephone',
      label: 'Téléphone',
      type: 'tel' as const,
      required: true
    },
    {
      id: 'zone',
      label: 'Zone de livraison',
      type: 'select' as const,
      required: true,
      placeholder: 'Sélectionnez votre zone...',
      options: [
        { value: 'ile-de-re', label: 'Île de Ré' },
        { value: 'montalivet', label: 'Montalivet' },
        { value: 'autre', label: 'Autre (à préciser dans le message)' }
      ]
    },
    {
      id: 'adresse',
      label: 'Adresse de livraison',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Adresse complète où livrer les vélos...',
      rows: 3
    },
    {
      id: 'dateDebut',
      label: 'Date souhaitée',
      type: 'date' as const,
      required: true
    },
    {
      id: 'dateFin',
      label: 'Date de retour',
      type: 'date' as const
    },
    {
      id: 'nombreVelos',
      label: 'Nombre de vélos',
      type: 'text' as const,
      required: true,
      placeholder: 'ex: 2 adultes, 1 enfant'
    },
    {
      id: 'typeVelo',
      label: 'Type de vélos souhaités',
      type: 'select' as const,
      options: [
        { value: 'classique', label: 'Vélos classiques' },
        { value: 'electrique', label: 'Vélos électriques' },
        { value: 'mixte', label: 'Mixte (classiques + électriques)' },
        { value: 'indifferent', label: 'Indifférent' }
      ]
    },
    {
      id: 'message',
      label: 'Message complémentaire',
      type: 'textarea' as const,
      placeholder: 'Précisez vos besoins, contraintes particulières...',
      rows: 4
    }
  ];

  const handleSubmit = async (data: Record<string, string>) => {
    await submitDeliveryRequest(data, data.email);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationParticuliers />
      
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Truck className="h-8 w-8" />
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold mb-6">
            Demande de livraison
          </h1>
          <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
            Faites-vous livrer vos vélos directement à votre hébergement sur l'Île de Ré ou Montalivet.
          </p>
        </div>
      </section>

      {/* Formulaire - Section prioritaire */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Remplissez votre demande
              </h2>
              <p className="text-slate-600">
                Nous vous recontacterons rapidement pour confirmer la disponibilité et les modalités.
              </p>
            </div>
            
            <UniformForm
              title="Demande de livraison vélo"
              description="Tous les champs marqués d'un * sont obligatoires"
              fields={formFields}
              submitText="Envoyer ma demande de livraison"
              onSubmit={handleSubmit}
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Informations complémentaires */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Comment ça marche ?
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  1. Zones couvertes
                </h3>
                <p className="text-slate-600 text-sm">
                  Livraison disponible sur l'Île de Ré et Montalivet. Autres zones sur demande.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  2. Délais
                </h3>
                <p className="text-slate-600 text-sm">
                  Commande à passer 48h à l'avance minimum. Livraison dans la journée convenue.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  3. Service inclus
                </h3>
                <p className="text-slate-600 text-sm">
                  Vélos entretenus, casques fournis, assistance téléphonique pendant la location.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default DemandeLivraison;